import React, { useMemo, useReducer } from 'react';

import DialogRenderer from './DialogRenderer';
import { DialogsContext } from './DialogsContext';
import { type DialogEntry, type DialogProps } from './types';

const initialStates: DialogEntry = {
	DialogComponent: null,
	dialogProps: null,
	isOpen: false,
};

interface Props {
	children: React.ReactNode;
}

interface Action {
	type: 'close' | 'open';
	payload?: Pick<DialogEntry, 'DialogComponent' | 'dialogProps'>;
}

function dialogReducer(state: DialogEntry, action: Action) {
	switch (action.type) {
		case 'open':
			return {
				...state,
				DialogComponent: action.payload?.DialogComponent || null,
				dialogProps: action.payload?.dialogProps || null,
				isOpen: true,
			};
		case 'close':
			return {
				...state,
				DialogComponent: null,
				dialogProps: null,
				isOpen: false,
			};
		default:
			throw new Error();
	}
}

export default function DialogsProvider(props: Props): React.JSX.Element {
	const { children } = props;
	const [stateDialogProvider, dispatch] = useReducer(dialogReducer, initialStates);

	const valueProvider = useMemo(
		() => ({
			openDialog: (
				DialogComponent: React.ComponentType<any> | null,
				dialogProps: DialogProps | null = null,
			) =>
				dispatch({
					type: 'open',
					payload: {
						DialogComponent,
						dialogProps,
					},
				}),
			closeDialog: () => dispatch({ type: 'close' }),
		}),
		[],
	);

	return (
		// @ts-ignore
		<DialogsContext.Provider value={valueProvider}>
			{children}
			<DialogRenderer {...stateDialogProvider} />
		</DialogsContext.Provider>
	);
}
